import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/Button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Building2,
  Download,
  Upload,
  FileText,
  Users,
  Clock,
  CheckCircle,
  Phone,
  Mail,
  Shield,
  Heart,
  Brain,
  Stethoscope,
  ClipboardList,
  FileCheck,
  AlertCircle,
  Star,
  Home,
  Building,
  Activity,
  Accessibility,
  User,
  Baby,
  GraduationCap,
  Ambulance,
  Hospital,
  HeartHandshake,
  UserCog,
  Users2,
  Bed,
  Plus,
  X,
  CalendarClock,
  Clipboard,
} from "lucide-react";

const documents = [
  {
    id: 1,
    title: "Psychiatric Evaluation Consent Form",
    description: "Required consent for comprehensive psychiatric assessment",
    type: "consent",
    pages: 3,
    lastUpdated: "2024-01-15",
  },
  {
    id: 2,
    title: "Telehealth Services Agreement",
    description: "Terms and conditions for remote psychiatric services",
    type: "agreement",
    pages: 5,
    lastUpdated: "2024-01-10",
  },
  {
    id: 3,
    title: "Medication Management Consent",
    description:
      "Authorization for psychiatric medication prescribing and monitoring",
    type: "consent",
    pages: 2,
    lastUpdated: "2024-01-12",
  },
  {
    id: 4,
    title: "HIPAA Privacy Notice",
    description: "Patient rights and privacy protection information",
    type: "notice",
    pages: 4,
    lastUpdated: "2024-01-08",
  },
  {
    id: 5,
    title: "Emergency Contact Authorization",
    description:
      "Permission to contact emergency contacts during crisis situations",
    type: "authorization",
    pages: 1,
    lastUpdated: "2024-01-14",
  },
  {
    id: 6,
    title: "Treatment Plan Agreement",
    description:
      "Collaborative treatment planning and goal setting documentation",
    type: "agreement",
    pages: 3,
    lastUpdated: "2024-01-16",
  },
];

const facilityTypes = [
  {
    category: "Long-Term Care Facilities",
    facilities: [
      {
        name: "Skilled Nursing Facilities",
        description:
          "24/7 medical care for residents requiring continuous nursing supervision",
        icon: Hospital,
        services: [
          "On-site psychiatric evaluations",
          "Medication management",
          "Crisis intervention",
          "Staff consultation",
        ],
        residents: "150-300 beds typically",
      },
      {
        name: "Nursing Homes",
        description:
          "Comprehensive care for elderly residents with complex medical needs",
        icon: Bed,
        services: [
          "Geriatric psychiatry",
          "Dementia behavioral management",
          "Family counseling",
          "End-of-life support",
        ],
        residents: "50-200 beds typically",
      },
      {
        name: "Memory Care Units",
        description:
          "Specialized care for residents with Alzheimer's and dementia",
        icon: Brain,
        services: [
          "Behavioral intervention",
          "Cognitive assessment",
          "Caregiver training",
          "Safety planning",
        ],
        residents: "20-60 beds typically",
      },
    ],
  },
  {
    category: "Assisted Living Communities",
    facilities: [
      {
        name: "Assisted Living Facilities",
        description:
          "Supportive housing for seniors who need help with daily activities",
        icon: Home,
        services: [
          "Mental health screening",
          "Medication compliance",
          "Social engagement programs",
          "Family support",
        ],
        residents: "50-150 units typically",
      },
      {
        name: "Independent Living Communities",
        description: "Active senior communities with optional support services",
        icon: Building,
        services: [
          "Wellness checks",
          "Preventive mental health",
          "Community programs",
          "Health education",
        ],
        residents: "100-400 units typically",
      },
      {
        name: "Continuing Care Retirement Communities",
        description:
          "Multi-level care campuses offering various levels of support",
        icon: Building2,
        services: [
          "Comprehensive psychiatric care",
          "Transition support",
          "Family consultation",
          "Staff training",
        ],
        residents: "200-1000+ residents",
      },
    ],
  },
  {
    category: "Specialized Care Facilities",
    facilities: [
      {
        name: "Rehabilitation Centers",
        description: "Short-term recovery and therapy facilities",
        icon: Activity,
        services: [
          "Post-acute psychiatric care",
          "Dual diagnosis treatment",
          "Discharge planning",
          "Therapeutic support",
        ],
        residents: "30-120 beds typically",
      },
      {
        name: "Adult Day Programs",
        description: "Daytime care and activities for adults with disabilities",
        icon: Users2,
        services: [
          "Behavioral support",
          "Mental health screening",
          "Caregiver relief",
          "Activity therapy",
        ],
        residents: "20-80 participants daily",
      },
      {
        name: "Group Homes",
        description:
          "Community-based residential care for individuals with disabilities",
        icon: HeartHandshake,
        services: [
          "Individualized care plans",
          "Behavioral intervention",
          "Community integration",
          "Family support",
        ],
        residents: "4-16 residents typically",
      },
    ],
  },
  {
    category: "Mental Health & Substance Abuse Facilities",
    facilities: [
      {
        name: "Residential Treatment Facilities",
        description:
          "Intensive residential mental health and substance abuse treatment",
        icon: Shield,
        services: [
          "Psychiatric evaluation",
          "Medication management",
          "Individual therapy",
          "Group therapy",
        ],
        residents: "20-100 beds typically",
      },
      {
        name: "Halfway Houses",
        description:
          "Transitional housing for individuals recovering from addiction",
        icon: UserCog,
        services: [
          "Dual diagnosis treatment",
          "Relapse prevention",
          "Life skills support",
          "Community reintegration",
        ],
        residents: "8-30 residents typically",
      },
      {
        name: "Crisis Residential Programs",
        description: "Short-term intensive support for mental health crises",
        icon: Ambulance,
        services: [
          "Crisis stabilization",
          "Safety planning",
          "Immediate intervention",
          "Discharge coordination",
        ],
        residents: "6-16 beds typically",
      },
    ],
  },
  {
    category: "Developmental & Intellectual Disability Services",
    facilities: [
      {
        name: "ICF/ID Facilities",
        description:
          "Intermediate Care Facilities for individuals with intellectual disabilities",
        icon: Accessibility,
        services: [
          "Behavioral support",
          "Psychiatric consultation",
          "Medication management",
          "Staff training",
        ],
        residents: "8-300+ residents",
      },
      {
        name: "Community Living Programs",
        description:
          "Community-based support for individuals with developmental disabilities",
        icon: Users,
        services: [
          "Behavioral intervention",
          "Mental health support",
          "Family consultation",
          "Crisis management",
        ],
        residents: "1-8 residents per home",
      },
      {
        name: "Day Habilitation Centers",
        description: "Day programs for adults with developmental disabilities",
        icon: GraduationCap,
        services: [
          "Behavioral support",
          "Mental health screening",
          "Skills training",
          "Family support",
        ],
        residents: "30-150 participants daily",
      },
    ],
  },
  {
    category: "Pediatric & Youth Facilities",
    facilities: [
      {
        name: "Pediatric Care Facilities",
        description: "Residential care for children with complex medical needs",
        icon: Baby,
        services: [
          "Child psychiatry",
          "Family therapy",
          "Behavioral intervention",
          "Educational support",
        ],
        residents: "10-50 beds typically",
      },
      {
        name: "Youth Residential Programs",
        description: "Therapeutic residential care for adolescents",
        icon: User,
        services: [
          "Adolescent psychiatry",
          "Family counseling",
          "Educational coordination",
          "Transition planning",
        ],
        residents: "12-40 beds typically",
      },
      {
        name: "Foster Care Agencies",
        description: "Support services for children in foster care placements",
        icon: Heart,
        services: [
          "Trauma-informed care",
          "Family therapy",
          "Crisis intervention",
          "Caregiver support",
        ],
        residents: "Various placements",
      },
    ],
  },
];

const referralTypes = [
  "Psychiatric Evaluation",
  "Medication Management",
  "Therapy Services",
  "Crisis Intervention",
  "Substance Abuse Treatment",
  "Geriatric Psychiatry",
  "Child & Adolescent Psychiatry",
  "Neuropsychological Testing",
];

const urgencyLevels = [
  {
    value: "routine",
    label: "Routine (2-4 weeks)",
    color: "bg-green-100 text-green-800",
  },
  {
    value: "urgent",
    label: "Urgent (1 week)",
    color: "bg-amber-100 text-amber-800",
  },
  {
    value: "emergent",
    label: "Emergent (24-48 hours)",
    color: "bg-red-100 text-red-800",
  },
];

const locumSpecialties = [
  "General Psychiatry",
  "Child & Adolescent Psychiatry",
  "Geriatric Psychiatry",
  "Addiction Psychiatry",
  "Forensic Psychiatry",
  "Neuropsychiatry",
  "Consultation-Liaison Psychiatry",
];

const coverageDurations = [
  { value: "short", label: "Short-term (1-4 weeks)" },
  { value: "medium", label: "Medium-term (1-3 months)" },
  { value: "long", label: "Long-term (3+ months)" },
  { value: "ongoing", label: "Ongoing/As needed" },
];

export const ForFacilities = () => {
  const [uploadFiles, setUploadFiles] = useState([]);
  const [referralMode, setReferralMode] = useState("single");

  const [singleReferralForm, setSingleReferralForm] = useState({
    patientName: "",
    patientDOB: "",
    facilityName: "",
    contactPerson: "",
    contactPhone: "",
    contactEmail: "",
    referralType: "",
    urgency: "",
    clinicalNotes: "",
    currentMedications: "",
    allergies: "",
    insuranceProvider: "",
    policyNumber: "",
    preferredProvider: "",
    preferredAppointmentTime: "",
  });

  // Multiple referral form state
  const [multipleReferralForm, setMultipleReferralForm] = useState({
    facilityName: "",
    contactPerson: "",
    contactPhone: "",
    contactEmail: "",
    generalNotes: "",
  });

  const [patients, setPatients] = useState([
    {
      id: "1",
      name: "",
      dateOfBirth: "",
      referralType: "",
      urgency: "",
      clinicalNotes: "",
      currentMedications: "",
      allergies: "",
      insuranceProvider: "",
      policyNumber: "",
    },
  ]);

  // Locum Tenens form state
  const [locumForm, setLocumForm] = useState({
    facilityName: "",
    contactPerson: "",
    contactPhone: "",
    contactEmail: "",
    specialty: "",
    coverageDuration: "",
    startDate: "",
    endDate: "",
    daysPerWeek: "",
    hoursPerDay: "",
    patientVolume: "",
    specialRequirements: "",
    certificationRequirements: "",
    compensation: "",
    housing: "",
    urgency: "routine",
    additionalNotes: "",
  });

  const handleDocumentDownload = (docId) => {
    const doc = documents.find((d) => d.id === docId);
    if (doc) {
      // In a real application, this would download the actual document
      const blob = new Blob([`${doc.title} - Document Content`], {
        type: "application/pdf",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${doc.title.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files || []);
    setUploadFiles((prev) => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setUploadFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const addPatient = () => {
    const newPatient = {
      id: Date.now().toString(),
      name: "",
      dateOfBirth: "",
      referralType: "",
      urgency: "",
      clinicalNotes: "",
      currentMedications: "",
      allergies: "",
      insuranceProvider: "",
      policyNumber: "",
    };
    setPatients((prev) => [...prev, newPatient]);
  };

  const removePatient = (patientId) => {
    if (patients.length > 1) {
      setPatients((prev) => prev.filter((p) => p.id !== patientId));
    }
  };

  const updatePatient = (patientId, field, value) => {
    setPatients((prev) =>
      prev.map((p) => (p.id === patientId ? { ...p, [field]: value } : p))
    );
  };

  const handleSingleReferralSubmit = (e) => {
    e.preventDefault();
    console.log("Single referral submitted:", singleReferralForm);
    // Handle single referral submission
  };

  const handleMultipleReferralSubmit = (e) => {
    e.preventDefault();
    console.log("Multiple referrals submitted:", {
      facilityInfo: multipleReferralForm,
      patients,
      uploadFiles,
    });
    // Handle multiple referral submission
  };

  const handleLocumSubmit = (e) => {
    e.preventDefault();
    console.log("Locum Tenens request submitted:", locumForm);
    // Handle locum tenens request submission
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-clinic-blue-50 to-clinic-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="icon-container icon-container-2xl icon-medical">
              <Building2 className="icon-4xl" />
            </div>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-clinic-gray-900 mb-6">
            For Healthcare Facilities
          </h1>
          <p className="text-xl text-clinic-gray-600 max-w-3xl mx-auto">
            Streamlined psychiatric services for nursing homes, assisted living
            facilities, and healthcare organizations serving your residents'
            mental health needs.
          </p>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="overview" className="space-y-8">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="facilities">Facility Types</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="referrals">Submit Referrals</TabsTrigger>
            <TabsTrigger value="contact">Contact Us</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-8">
            {/* Service Overview */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <Card className="card-elevated">
                <CardHeader>
                  <div className="icon-container icon-container-lg icon-medical mb-4">
                    <Stethoscope className="icon-2xl" />
                  </div>
                  <CardTitle>On-Site Psychiatric Services</CardTitle>
                  <CardDescription>
                    Licensed psychiatrists visit your facility to provide
                    comprehensive mental health care
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-clinic-gray-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Psychiatric evaluations
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Medication management
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Crisis intervention
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Staff consultation
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardHeader>
                  <div className="icon-container icon-container-lg icon-therapy mb-4">
                    <Brain className="icon-2xl" />
                  </div>
                  <CardTitle>Telehealth Options</CardTitle>
                  <CardDescription>
                    Remote psychiatric consultations for residents when
                    appropriate
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-clinic-gray-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Secure video consultations
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Medication adjustments
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Follow-up appointments
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Emergency consultations
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardHeader>
                  <div className="icon-container icon-container-lg icon-success mb-4">
                    <Shield className="icon-2xl" />
                  </div>
                  <CardTitle>HIPAA Compliant</CardTitle>
                  <CardDescription>
                    Full compliance with healthcare privacy and security
                    regulations
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-clinic-gray-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Secure documentation
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Protected communications
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Confidential records
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Staff training support
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Process Steps */}
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <ClipboardList className="icon-lg text-clinic-blue-600" />
                  How Our Process Works
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="icon-container icon-container-xl icon-medical mx-auto mb-4">
                      <span className="text-2xl font-bold">1</span>
                    </div>
                    <h3 className="font-semibold mb-2">Submit Referral</h3>
                    <p className="text-sm text-clinic-gray-600">
                      Complete our referral form with resident information
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="icon-container icon-container-xl icon-medical mx-auto mb-4">
                      <span className="text-2xl font-bold">2</span>
                    </div>
                    <h3 className="font-semibold mb-2">Review & Schedule</h3>
                    <p className="text-sm text-clinic-gray-600">
                      We review the case and schedule appropriate services
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="icon-container icon-container-xl icon-medical mx-auto mb-4">
                      <span className="text-2xl font-bold">3</span>
                    </div>
                    <h3 className="font-semibold mb-2">Provide Care</h3>
                    <p className="text-sm text-clinic-gray-600">
                      Our psychiatrists deliver comprehensive care
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="icon-container icon-container-xl icon-medical mx-auto mb-4">
                      <span className="text-2xl font-bold">4</span>
                    </div>
                    <h3 className="font-semibold mb-2">Ongoing Support</h3>
                    <p className="text-sm text-clinic-gray-600">
                      Continuous monitoring and follow-up care
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Facility Types Tab */}
          <TabsContent value="facilities" className="space-y-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-clinic-gray-900 mb-4">
                Facility Types We Support
              </h2>
              <p className="text-xl text-clinic-gray-600 max-w-3xl mx-auto">
                We provide specialized psychiatric services to a wide range of
                healthcare and residential facilities, tailored to meet the
                unique needs of each care setting and population.
              </p>
            </div>

            {facilityTypes.map((category, categoryIndex) => (
              <div key={categoryIndex} className="space-y-6">
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-clinic-blue-800 mb-2">
                    {category.category}
                  </h3>
                  <div className="w-24 h-1 bg-clinic-blue-600 mx-auto rounded-full"></div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {category.facilities.map((facility, facilityIndex) => (
                    <Card
                      key={facilityIndex}
                      className="card-elevated card-interactive h-full"
                    >
                      <CardHeader>
                        <div className="icon-container icon-container-lg icon-medical mb-4">
                          <facility.icon className="icon-2xl" />
                        </div>
                        <CardTitle className="text-xl">
                          {facility.name}
                        </CardTitle>
                        <CardDescription className="text-base">
                          {facility.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <h4 className="font-semibold text-clinic-gray-800 mb-2">
                            Our Services Include:
                          </h4>
                          <ul className="space-y-1">
                            {facility.services.map((service, serviceIndex) => (
                              <li
                                key={serviceIndex}
                                className="flex items-center gap-2"
                              >
                                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                <span className="text-sm text-clinic-gray-600">
                                  {service}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="pt-3 border-t border-clinic-gray-200">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-clinic-blue-600" />
                            <span className="text-sm text-clinic-gray-500">
                              Capacity:{" "}
                            </span>
                            <span className="text-sm font-medium text-clinic-gray-700">
                              {facility.residents}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}

            {/* Additional Information */}
            <Card className="card-elevated bg-clinic-blue-50">
              <CardContent className="p-8">
                <div className="text-center">
                  <div className="icon-container icon-container-xl icon-medical mx-auto mb-6">
                    <Heart className="icon-3xl" />
                  </div>
                  <h3 className="text-2xl font-bold text-clinic-blue-800 mb-4">
                    Don't See Your Facility Type?
                  </h3>
                  <p className="text-clinic-blue-700 mb-6 max-w-2xl mx-auto">
                    We work with many different types of care facilities and are
                    always open to discussing how our psychiatric services can
                    support your unique resident population and care model.
                  </p>
                  <Button className="btn-lg bg-clinic-blue-600 hover:bg-clinic-blue-700 text-white">
                    <Phone className="w-5 h-5 mr-2" />
                    Contact Us to Discuss Your Needs
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-6">
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <FileText className="icon-lg text-clinic-blue-600" />
                  Required Documents & Forms
                </CardTitle>
                <CardDescription>
                  Download necessary forms and documents for resident
                  psychiatric services
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {documents.map((doc) => (
                    <Card
                      key={doc.id}
                      className="border-l-4 border-l-clinic-blue-500"
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-lg">
                              {doc.title}
                            </CardTitle>
                            <CardDescription className="text-sm">
                              {doc.description}
                            </CardDescription>
                          </div>
                          <Badge
                            variant={
                              doc.type === "consent" ? "default" : "secondary"
                            }
                          >
                            {doc.type}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="flex items-center justify-between text-sm text-clinic-gray-500 mb-3">
                          <span>{doc.pages} pages</span>
                          <span>Updated: {doc.lastUpdated}</span>
                        </div>
                        <Button
                          onClick={() => handleDocumentDownload(doc.id)}
                          className="w-full btn-md"
                          variant="outline"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download PDF
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Document Instructions */}
            <Card className="card-elevated bg-clinic-blue-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-clinic-blue-800">
                  <AlertCircle className="icon-lg" />
                  Important Instructions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-clinic-blue-700">
                  <p>
                    • All consent forms must be completed and signed by the
                    resident or their legal guardian before services begin
                  </p>
                  <p>
                    • Please ensure forms are legible and completely filled out
                    to avoid delays in service delivery
                  </p>
                  <p>
                    • Emergency contact information must be current and accurate
                  </p>
                  <p>
                    • Return completed forms via secure upload or fax to
                    maintain HIPAA compliance
                  </p>
                  <p>
                    • Contact our office if you need assistance completing any
                    forms
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Enhanced Referrals Tab */}
          <TabsContent value="referrals" className="space-y-6">
            {/* Referral Type Selector */}
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <Clipboard className="icon-lg text-clinic-blue-600" />
                  Choose Your Request Type
                </CardTitle>
                <CardDescription>
                  Select the type of service request you'd like to submit
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  <Card
                    className={`cursor-pointer transition-all duration-200 ${
                      referralMode === "single"
                        ? "ring-2 ring-clinic-blue-500 bg-clinic-blue-50"
                        : "hover:shadow-md"
                    }`}
                    onClick={() => setReferralMode("single")}
                  >
                    <CardContent className="p-6 text-center">
                      <div className="icon-container icon-container-lg icon-medical mx-auto mb-4">
                        <User className="icon-2xl" />
                      </div>
                      <h3 className="font-semibold mb-2">
                        Single Client Referral
                      </h3>
                      <p className="text-sm text-clinic-gray-600">
                        Refer one client for psychiatric services
                      </p>
                    </CardContent>
                  </Card>

                  <Card
                    className={`cursor-pointer transition-all duration-200 ${
                      referralMode === "multiple"
                        ? "ring-2 ring-clinic-blue-500 bg-clinic-blue-50"
                        : "hover:shadow-md"
                    }`}
                    onClick={() => setReferralMode("multiple")}
                  >
                    <CardContent className="p-6 text-center">
                      <div className="icon-container icon-container-lg icon-medical mx-auto mb-4">
                        <Users className="icon-2xl" />
                      </div>
                      <h3 className="font-semibold mb-2">
                        Multiple Client Referrals
                      </h3>
                      <p className="text-sm text-clinic-gray-600">
                        Refer multiple clients in one submission
                      </p>
                    </CardContent>
                  </Card>

                  <Card
                    className={`cursor-pointer transition-all duration-200 ${
                      referralMode === "locum"
                        ? "ring-2 ring-clinic-blue-500 bg-clinic-blue-50"
                        : "hover:shadow-md"
                    }`}
                    onClick={() => setReferralMode("locum")}
                  >
                    <CardContent className="p-6 text-center">
                      <div className="icon-container icon-container-lg icon-warning mx-auto mb-4">
                        <CalendarClock className="icon-2xl" />
                      </div>
                      <h3 className="font-semibold mb-2">
                        Locum Tenens Request
                      </h3>
                      <p className="text-sm text-clinic-gray-600">
                        Request temporary provider coverage
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>

            {/* Single Referral Form */}
            {referralMode === "single" && (
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <User className="icon-lg text-clinic-blue-600" />
                    Single Client Referral
                  </CardTitle>
                  <CardDescription>
                    Submit a referral request for one client
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form
                    onSubmit={handleSingleReferralSubmit}
                    className="space-y-6"
                  >
                    {/* Facility Information */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-clinic-gray-800">
                        Facility Information
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="facilityName">Facility Name *</Label>
                          <Input
                            id="facilityName"
                            value={singleReferralForm.facilityName}
                            onChange={(e) =>
                              setSingleReferralForm((prev) => ({
                                ...prev,
                                facilityName: e.target.value,
                              }))
                            }
                            required
                            className="input-medical"
                          />
                        </div>
                        <div>
                          <Label htmlFor="contactPerson">
                            Contact Person *
                          </Label>
                          <Input
                            id="contactPerson"
                            value={singleReferralForm.contactPerson}
                            onChange={(e) =>
                              setSingleReferralForm((prev) => ({
                                ...prev,
                                contactPerson: e.target.value,
                              }))
                            }
                            required
                            className="input-medical"
                          />
                        </div>
                        <div>
                          <Label htmlFor="contactPhone">Phone Number *</Label>
                          <Input
                            id="contactPhone"
                            type="tel"
                            value={singleReferralForm.contactPhone}
                            onChange={(e) =>
                              setSingleReferralForm((prev) => ({
                                ...prev,
                                contactPhone: e.target.value,
                              }))
                            }
                            required
                            className="input-medical"
                          />
                        </div>
                        <div>
                          <Label htmlFor="contactEmail">Email Address *</Label>
                          <Input
                            id="contactEmail"
                            type="email"
                            value={singleReferralForm.contactEmail}
                            onChange={(e) =>
                              setSingleReferralForm((prev) => ({
                                ...prev,
                                contactEmail: e.target.value,
                              }))
                            }
                            required
                            className="input-medical"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Patient Information */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-clinic-gray-800">
                        Patient Information
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="patientName">Patient Name *</Label>
                          <Input
                            id="patientName"
                            value={singleReferralForm.patientName}
                            onChange={(e) =>
                              setSingleReferralForm((prev) => ({
                                ...prev,
                                patientName: e.target.value,
                              }))
                            }
                            required
                            className="input-medical"
                          />
                        </div>
                        <div>
                          <Label htmlFor="patientDOB">Date of Birth *</Label>
                          <Input
                            id="patientDOB"
                            type="date"
                            value={singleReferralForm.patientDOB}
                            onChange={(e) =>
                              setSingleReferralForm((prev) => ({
                                ...prev,
                                patientDOB: e.target.value,
                              }))
                            }
                            required
                            className="input-medical"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Service Details */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-clinic-gray-800">
                        Service Request Details
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="referralType">
                            Type of Service Requested *
                          </Label>
                          <Select
                            value={singleReferralForm.referralType}
                            onValueChange={(value) =>
                              setSingleReferralForm((prev) => ({
                                ...prev,
                                referralType: value,
                              }))
                            }
                          >
                            <SelectTrigger className="input-medical">
                              <SelectValue placeholder="Select service type" />
                            </SelectTrigger>
                            <SelectContent>
                              {referralTypes.map((type) => (
                                <SelectItem key={type} value={type}>
                                  {type}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="urgency">Urgency Level *</Label>
                          <Select
                            value={singleReferralForm.urgency}
                            onValueChange={(value) =>
                              setSingleReferralForm((prev) => ({
                                ...prev,
                                urgency: value,
                              }))
                            }
                          >
                            <SelectTrigger className="input-medical">
                              <SelectValue placeholder="Select urgency" />
                            </SelectTrigger>
                            <SelectContent>
                              {urgencyLevels.map((level) => (
                                <SelectItem
                                  key={level.value}
                                  value={level.value}
                                >
                                  <div className="flex items-center gap-2">
                                    <span
                                      className={`px-2 py-1 rounded-full text-xs ${level.color}`}
                                    >
                                      {level.label}
                                    </span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {/* Clinical Information */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-clinic-gray-800">
                        Clinical Information
                      </h3>
                      <div>
                        <Label htmlFor="clinicalNotes">
                          Clinical Notes & Reason for Referral *
                        </Label>
                        <Textarea
                          id="clinicalNotes"
                          value={singleReferralForm.clinicalNotes}
                          onChange={(e) =>
                            setSingleReferralForm((prev) => ({
                              ...prev,
                              clinicalNotes: e.target.value,
                            }))
                          }
                          placeholder="Please provide detailed clinical information, symptoms, behavioral concerns, and reason for psychiatric referral..."
                          rows={4}
                          required
                          className="input-medical"
                        />
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="currentMedications">
                            Current Medications
                          </Label>
                          <Textarea
                            id="currentMedications"
                            value={singleReferralForm.currentMedications}
                            onChange={(e) =>
                              setSingleReferralForm((prev) => ({
                                ...prev,
                                currentMedications: e.target.value,
                              }))
                            }
                            placeholder="List all current medications, dosages, and prescribing physicians..."
                            rows={3}
                            className="input-medical"
                          />
                        </div>
                        <div>
                          <Label htmlFor="allergies">Known Allergies</Label>
                          <Textarea
                            id="allergies"
                            value={singleReferralForm.allergies}
                            onChange={(e) =>
                              setSingleReferralForm((prev) => ({
                                ...prev,
                                allergies: e.target.value,
                              }))
                            }
                            placeholder="List any known allergies to medications or other substances..."
                            rows={3}
                            className="input-medical"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Insurance Information */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-clinic-gray-800">
                        Insurance Information
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="insuranceProvider">
                            Insurance Provider
                          </Label>
                          <Input
                            id="insuranceProvider"
                            value={singleReferralForm.insuranceProvider}
                            onChange={(e) =>
                              setSingleReferralForm((prev) => ({
                                ...prev,
                                insuranceProvider: e.target.value,
                              }))
                            }
                            placeholder="Medicare, Medicaid, Private insurance..."
                            className="input-medical"
                          />
                        </div>
                        <div>
                          <Label htmlFor="policyNumber">
                            Policy/Member Number
                          </Label>
                          <Input
                            id="policyNumber"
                            value={singleReferralForm.policyNumber}
                            onChange={(e) =>
                              setSingleReferralForm((prev) => ({
                                ...prev,
                                policyNumber: e.target.value,
                              }))
                            }
                            className="input-medical"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-end pt-6">
                      <Button
                        type="submit"
                        className="btn-lg bg-clinic-blue-600 hover:bg-clinic-blue-700"
                      >
                        <FileCheck className="w-5 h-5 mr-2" />
                        Submit Single Referral
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Multiple Referrals Form */}
            {referralMode === "multiple" && (
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Users className="icon-lg text-clinic-blue-600" />
                    Multiple Client Referrals
                  </CardTitle>
                  <CardDescription>
                    Submit referrals for multiple clients in one submission
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form
                    onSubmit={handleMultipleReferralSubmit}
                    className="space-y-6"
                  >
                    {/* Facility Information */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-clinic-gray-800">
                        Facility Information
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="multiFacilityName">
                            Facility Name *
                          </Label>
                          <Input
                            id="multiFacilityName"
                            value={multipleReferralForm.facilityName}
                            onChange={(e) =>
                              setMultipleReferralForm((prev) => ({
                                ...prev,
                                facilityName: e.target.value,
                              }))
                            }
                            required
                            className="input-medical"
                          />
                        </div>
                        <div>
                          <Label htmlFor="multiContactPerson">
                            Contact Person *
                          </Label>
                          <Input
                            id="multiContactPerson"
                            value={multipleReferralForm.contactPerson}
                            onChange={(e) =>
                              setMultipleReferralForm((prev) => ({
                                ...prev,
                                contactPerson: e.target.value,
                              }))
                            }
                            required
                            className="input-medical"
                          />
                        </div>
                        <div>
                          <Label htmlFor="multiContactPhone">
                            Phone Number *
                          </Label>
                          <Input
                            id="multiContactPhone"
                            type="tel"
                            value={multipleReferralForm.contactPhone}
                            onChange={(e) =>
                              setMultipleReferralForm((prev) => ({
                                ...prev,
                                contactPhone: e.target.value,
                              }))
                            }
                            required
                            className="input-medical"
                          />
                        </div>
                        <div>
                          <Label htmlFor="multiContactEmail">
                            Email Address *
                          </Label>
                          <Input
                            id="multiContactEmail"
                            type="email"
                            value={multipleReferralForm.contactEmail}
                            onChange={(e) =>
                              setMultipleReferralForm((prev) => ({
                                ...prev,
                                contactEmail: e.target.value,
                              }))
                            }
                            required
                            className="input-medical"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="generalNotes">General Notes</Label>
                        <Textarea
                          id="generalNotes"
                          value={multipleReferralForm.generalNotes}
                          onChange={(e) =>
                            setMultipleReferralForm((prev) => ({
                              ...prev,
                              generalNotes: e.target.value,
                            }))
                          }
                          placeholder="Any general notes about these referrals or your facility's needs..."
                          rows={3}
                          className="input-medical"
                        />
                      </div>
                    </div>

                    {/* Multiple Patients Section */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-clinic-gray-800">
                          Patient Information
                        </h3>
                        <Button
                          type="button"
                          onClick={addPatient}
                          variant="outline"
                          className="btn-md"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Patient
                        </Button>
                      </div>

                      {patients.map((patient, index) => (
                        <Card
                          key={patient.id}
                          className="border-l-4 border-l-clinic-blue-500"
                        >
                          <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-lg">
                                Patient {index + 1}
                              </CardTitle>
                              {patients.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removePatient(patient.id)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor={`patient-name-${patient.id}`}>
                                  Patient Name *
                                </Label>
                                <Input
                                  id={`patient-name-${patient.id}`}
                                  value={patient.name}
                                  onChange={(e) =>
                                    updatePatient(
                                      patient.id,
                                      "name",
                                      e.target.value
                                    )
                                  }
                                  required
                                  className="input-medical"
                                />
                              </div>
                              <div>
                                <Label htmlFor={`patient-dob-${patient.id}`}>
                                  Date of Birth *
                                </Label>
                                <Input
                                  id={`patient-dob-${patient.id}`}
                                  type="date"
                                  value={patient.dateOfBirth}
                                  onChange={(e) =>
                                    updatePatient(
                                      patient.id,
                                      "dateOfBirth",
                                      e.target.value
                                    )
                                  }
                                  required
                                  className="input-medical"
                                />
                              </div>
                              <div>
                                <Label
                                  htmlFor={`patient-referral-${patient.id}`}
                                >
                                  Service Type *
                                </Label>
                                <Select
                                  value={patient.referralType}
                                  onValueChange={(value) =>
                                    updatePatient(
                                      patient.id,
                                      "referralType",
                                      value
                                    )
                                  }
                                >
                                  <SelectTrigger className="input-medical">
                                    <SelectValue placeholder="Select service type" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {referralTypes.map((type) => (
                                      <SelectItem key={type} value={type}>
                                        {type}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label
                                  htmlFor={`patient-urgency-${patient.id}`}
                                >
                                  Urgency *
                                </Label>
                                <Select
                                  value={patient.urgency}
                                  onValueChange={(value) =>
                                    updatePatient(patient.id, "urgency", value)
                                  }
                                >
                                  <SelectTrigger className="input-medical">
                                    <SelectValue placeholder="Select urgency" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {urgencyLevels.map((level) => (
                                      <SelectItem
                                        key={level.value}
                                        value={level.value}
                                      >
                                        <span
                                          className={`px-2 py-1 rounded-full text-xs ${level.color}`}
                                        >
                                          {level.label}
                                        </span>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <div>
                              <Label htmlFor={`patient-notes-${patient.id}`}>
                                Clinical Notes *
                              </Label>
                              <Textarea
                                id={`patient-notes-${patient.id}`}
                                value={patient.clinicalNotes}
                                onChange={(e) =>
                                  updatePatient(
                                    patient.id,
                                    "clinicalNotes",
                                    e.target.value
                                  )
                                }
                                placeholder="Clinical information, symptoms, and reason for referral..."
                                rows={3}
                                required
                                className="input-medical"
                              />
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor={`patient-meds-${patient.id}`}>
                                  Current Medications
                                </Label>
                                <Textarea
                                  id={`patient-meds-${patient.id}`}
                                  value={patient.currentMedications}
                                  onChange={(e) =>
                                    updatePatient(
                                      patient.id,
                                      "currentMedications",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Current medications and dosages..."
                                  rows={2}
                                  className="input-medical"
                                />
                              </div>
                              <div>
                                <Label
                                  htmlFor={`patient-allergies-${patient.id}`}
                                >
                                  Known Allergies
                                </Label>
                                <Textarea
                                  id={`patient-allergies-${patient.id}`}
                                  value={patient.allergies}
                                  onChange={(e) =>
                                    updatePatient(
                                      patient.id,
                                      "allergies",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Known allergies..."
                                  rows={2}
                                  className="input-medical"
                                />
                              </div>
                              <div>
                                <Label
                                  htmlFor={`patient-insurance-${patient.id}`}
                                >
                                  Insurance Provider
                                </Label>
                                <Input
                                  id={`patient-insurance-${patient.id}`}
                                  value={patient.insuranceProvider}
                                  onChange={(e) =>
                                    updatePatient(
                                      patient.id,
                                      "insuranceProvider",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Medicare, Medicaid, etc..."
                                  className="input-medical"
                                />
                              </div>
                              <div>
                                <Label htmlFor={`patient-policy-${patient.id}`}>
                                  Policy Number
                                </Label>
                                <Input
                                  id={`patient-policy-${patient.id}`}
                                  value={patient.policyNumber}
                                  onChange={(e) =>
                                    updatePatient(
                                      patient.id,
                                      "policyNumber",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Policy/Member number..."
                                  className="input-medical"
                                />
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {/* File Upload */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-clinic-gray-800">
                        Upload Supporting Documents
                      </h3>
                      <div className="border-2 border-dashed border-clinic-gray-300 rounded-lg p-6 text-center">
                        <Upload className="mx-auto h-12 w-12 text-clinic-gray-400 mb-4" />
                        <p className="text-clinic-gray-600 mb-4">
                          Upload physician referrals, medical records, or other
                          supporting documents
                        </p>
                        <input
                          type="file"
                          id="multi-file-upload"
                          multiple
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={handleFileUpload}
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() =>
                            document
                              .getElementById("multi-file-upload")
                              ?.click()
                          }
                          className="btn-md"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Choose Files
                        </Button>
                      </div>

                      {uploadFiles.length > 0 && (
                        <div className="space-y-2">
                          <p className="font-medium text-clinic-gray-700">
                            Uploaded Files:
                          </p>
                          {uploadFiles.map((file, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between bg-clinic-gray-50 p-3 rounded-lg"
                            >
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-clinic-blue-600" />
                                <span className="text-sm">{file.name}</span>
                                <span className="text-xs text-clinic-gray-500">
                                  ({Math.round(file.size / 1024)} KB)
                                </span>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeFile(index)}
                                className="text-red-600 hover:text-red-700"
                              >
                                Remove
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-end pt-6">
                      <Button
                        type="submit"
                        className="btn-lg bg-clinic-blue-600 hover:bg-clinic-blue-700"
                      >
                        <FileCheck className="w-5 h-5 mr-2" />
                        Submit Multiple Referrals ({patients.length} patients)
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Locum Tenens Form */}
            {referralMode === "locum" && (
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <CalendarClock className="icon-lg text-clinic-gold-600" />
                    Locum Tenens Request
                  </CardTitle>
                  <CardDescription>
                    Request temporary psychiatric provider coverage for your
                    facility
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLocumSubmit} className="space-y-6">
                    {/* Facility Information */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-clinic-gray-800">
                        Facility Information
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="locumFacilityName">
                            Facility Name *
                          </Label>
                          <Input
                            id="locumFacilityName"
                            value={locumForm.facilityName}
                            onChange={(e) =>
                              setLocumForm((prev) => ({
                                ...prev,
                                facilityName: e.target.value,
                              }))
                            }
                            required
                            className="input-medical"
                          />
                        </div>
                        <div>
                          <Label htmlFor="locumContactPerson">
                            Contact Person *
                          </Label>
                          <Input
                            id="locumContactPerson"
                            value={locumForm.contactPerson}
                            onChange={(e) =>
                              setLocumForm((prev) => ({
                                ...prev,
                                contactPerson: e.target.value,
                              }))
                            }
                            required
                            className="input-medical"
                          />
                        </div>
                        <div>
                          <Label htmlFor="locumContactPhone">
                            Phone Number *
                          </Label>
                          <Input
                            id="locumContactPhone"
                            type="tel"
                            value={locumForm.contactPhone}
                            onChange={(e) =>
                              setLocumForm((prev) => ({
                                ...prev,
                                contactPhone: e.target.value,
                              }))
                            }
                            required
                            className="input-medical"
                          />
                        </div>
                        <div>
                          <Label htmlFor="locumContactEmail">
                            Email Address *
                          </Label>
                          <Input
                            id="locumContactEmail"
                            type="email"
                            value={locumForm.contactEmail}
                            onChange={(e) =>
                              setLocumForm((prev) => ({
                                ...prev,
                                contactEmail: e.target.value,
                              }))
                            }
                            required
                            className="input-medical"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Coverage Details */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-clinic-gray-800">
                        Coverage Requirements
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="specialty">
                            Specialty Required *
                          </Label>
                          <Select
                            value={locumForm.specialty}
                            onValueChange={(value) =>
                              setLocumForm((prev) => ({
                                ...prev,
                                specialty: value,
                              }))
                            }
                          >
                            <SelectTrigger className="input-medical">
                              <SelectValue placeholder="Select specialty" />
                            </SelectTrigger>
                            <SelectContent>
                              {locumSpecialties.map((specialty) => (
                                <SelectItem key={specialty} value={specialty}>
                                  {specialty}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="coverageDuration">
                            Coverage Duration *
                          </Label>
                          <Select
                            value={locumForm.coverageDuration}
                            onValueChange={(value) =>
                              setLocumForm((prev) => ({
                                ...prev,
                                coverageDuration: value,
                              }))
                            }
                          >
                            <SelectTrigger className="input-medical">
                              <SelectValue placeholder="Select duration" />
                            </SelectTrigger>
                            <SelectContent>
                              {coverageDurations.map((duration) => (
                                <SelectItem
                                  key={duration.value}
                                  value={duration.value}
                                >
                                  {duration.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="startDate">
                            Preferred Start Date *
                          </Label>
                          <Input
                            id="startDate"
                            type="date"
                            value={locumForm.startDate}
                            onChange={(e) =>
                              setLocumForm((prev) => ({
                                ...prev,
                                startDate: e.target.value,
                              }))
                            }
                            required
                            className="input-medical"
                          />
                        </div>
                        <div>
                          <Label htmlFor="endDate">Estimated End Date</Label>
                          <Input
                            id="endDate"
                            type="date"
                            value={locumForm.endDate}
                            onChange={(e) =>
                              setLocumForm((prev) => ({
                                ...prev,
                                endDate: e.target.value,
                              }))
                            }
                            className="input-medical"
                          />
                        </div>
                        <div>
                          <Label htmlFor="daysPerWeek">Days Per Week *</Label>
                          <Select
                            value={locumForm.daysPerWeek}
                            onValueChange={(value) =>
                              setLocumForm((prev) => ({
                                ...prev,
                                daysPerWeek: value,
                              }))
                            }
                          >
                            <SelectTrigger className="input-medical">
                              <SelectValue placeholder="Select days per week" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">1 day per week</SelectItem>
                              <SelectItem value="2">2 days per week</SelectItem>
                              <SelectItem value="3">3 days per week</SelectItem>
                              <SelectItem value="4">4 days per week</SelectItem>
                              <SelectItem value="5">
                                5 days per week (Full-time)
                              </SelectItem>
                              <SelectItem value="flexible">
                                Flexible schedule
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="hoursPerDay">Hours Per Day *</Label>
                          <Select
                            value={locumForm.hoursPerDay}
                            onValueChange={(value) =>
                              setLocumForm((prev) => ({
                                ...prev,
                                hoursPerDay: value,
                              }))
                            }
                          >
                            <SelectTrigger className="input-medical">
                              <SelectValue placeholder="Select hours per day" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="4">4 hours</SelectItem>
                              <SelectItem value="6">6 hours</SelectItem>
                              <SelectItem value="8">
                                8 hours (Full day)
                              </SelectItem>
                              <SelectItem value="10">10 hours</SelectItem>
                              <SelectItem value="12">12 hours</SelectItem>
                              <SelectItem value="flexible">
                                Flexible hours
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {/* Additional Requirements */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-clinic-gray-800">
                        Additional Information
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="patientVolume">
                            Expected Patient Volume per Day
                          </Label>
                          <Input
                            id="patientVolume"
                            value={locumForm.patientVolume}
                            onChange={(e) =>
                              setLocumForm((prev) => ({
                                ...prev,
                                patientVolume: e.target.value,
                              }))
                            }
                            placeholder="e.g., 15-20 patients"
                            className="input-medical"
                          />
                        </div>
                        <div>
                          <Label htmlFor="compensation">
                            Compensation Range
                          </Label>
                          <Input
                            id="compensation"
                            value={locumForm.compensation}
                            onChange={(e) =>
                              setLocumForm((prev) => ({
                                ...prev,
                                compensation: e.target.value,
                              }))
                            }
                            placeholder="e.g., $2,000-2,500 per day"
                            className="input-medical"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="certificationRequirements">
                          Certification Requirements *
                        </Label>
                        <Textarea
                          id="certificationRequirements"
                          value={locumForm.certificationRequirements}
                          onChange={(e) =>
                            setLocumForm((prev) => ({
                              ...prev,
                              certificationRequirements: e.target.value,
                            }))
                          }
                          placeholder="List required certifications, licenses, and credentials (e.g., Board certification in Psychiatry, State medical license, DEA registration, etc.)"
                          rows={3}
                          required
                          className="input-medical"
                        />
                      </div>
                      <div>
                        <Label htmlFor="specialRequirements">
                          Special Requirements & Preferences
                        </Label>
                        <Textarea
                          id="specialRequirements"
                          value={locumForm.specialRequirements}
                          onChange={(e) =>
                            setLocumForm((prev) => ({
                              ...prev,
                              specialRequirements: e.target.value,
                            }))
                          }
                          placeholder="Any special requirements, preferences for provider characteristics, experience with specific populations, etc."
                          rows={3}
                          className="input-medical"
                        />
                      </div>
                      <div>
                        <Label htmlFor="housing">
                          Housing & Travel Assistance
                        </Label>
                        <Select
                          value={locumForm.housing}
                          onValueChange={(value) =>
                            setLocumForm((prev) => ({
                              ...prev,
                              housing: value,
                            }))
                          }
                        >
                          <SelectTrigger className="input-medical">
                            <SelectValue placeholder="Select housing options" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="provided">
                              Housing provided by facility
                            </SelectItem>
                            <SelectItem value="allowance">
                              Housing allowance provided
                            </SelectItem>
                            <SelectItem value="none">
                              No housing assistance
                            </SelectItem>
                            <SelectItem value="discuss">
                              To be discussed
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="locumUrgency">Request Urgency</Label>
                        <Select
                          value={locumForm.urgency}
                          onValueChange={(value) =>
                            setLocumForm((prev) => ({
                              ...prev,
                              urgency: value,
                            }))
                          }
                        >
                          <SelectTrigger className="input-medical">
                            <SelectValue placeholder="Select urgency" />
                          </SelectTrigger>
                          <SelectContent>
                            {urgencyLevels.map((level) => (
                              <SelectItem key={level.value} value={level.value}>
                                <span
                                  className={`px-2 py-1 rounded-full text-xs ${level.color}`}
                                >
                                  {level.label}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="additionalNotes">
                          Additional Notes
                        </Label>
                        <Textarea
                          id="additionalNotes"
                          value={locumForm.additionalNotes}
                          onChange={(e) =>
                            setLocumForm((prev) => ({
                              ...prev,
                              additionalNotes: e.target.value,
                            }))
                          }
                          placeholder="Any additional information about your facility, patient population, or specific needs..."
                          rows={4}
                          className="input-medical"
                        />
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-end pt-6">
                      <Button
                        type="submit"
                        className="btn-lg bg-clinic-gold-600 hover:bg-clinic-gold-700 text-white"
                      >
                        <CalendarClock className="w-5 h-5 mr-2" />
                        Submit Locum Tenens Request
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Contact Tab */}
          <TabsContent value="contact" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Phone className="icon-lg text-clinic-blue-600" />
                    Contact Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-clinic-blue-600" />
                    <div>
                      <p className="font-medium">Facility Services Line</p>
                      <p className="text-clinic-gray-600">(555) 123-4567</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-clinic-blue-600" />
                    <div>
                      <p className="font-medium">Referrals Email</p>
                      <p className="text-clinic-gray-600">
                        facilities@ennvital.com
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <CalendarClock className="w-5 h-5 text-clinic-gold-600" />
                    <div>
                      <p className="font-medium">Locum Tenens Requests</p>
                      <p className="text-clinic-gray-600">locum@ennvital.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-clinic-blue-600" />
                    <div>
                      <p className="font-medium">Business Hours</p>
                      <p className="text-clinic-gray-600">
                        Mon-Fri: 8:00 AM - 6:00 PM
                      </p>
                      <p className="text-clinic-gray-600">
                        Emergency: 24/7 on-call
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Star className="icon-lg text-clinic-gold-600" />
                    Why Choose Our Services
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <p className="font-medium">
                        Board-Certified Psychiatrists
                      </p>
                      <p className="text-sm text-clinic-gray-600">
                        Experienced in geriatric and facility-based care
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <p className="font-medium">Flexible Scheduling</p>
                      <p className="text-sm text-clinic-gray-600">
                        On-site visits and telehealth options
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <p className="font-medium">Locum Tenens Coverage</p>
                      <p className="text-sm text-clinic-gray-600">
                        Temporary staffing solutions when you need them
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <p className="font-medium">HIPAA Compliant</p>
                      <p className="text-sm text-clinic-gray-600">
                        Secure documentation and communication
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <p className="font-medium">Insurance Accepted</p>
                      <p className="text-sm text-clinic-gray-600">
                        Medicare, Medicaid, and most major insurers
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
